import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Badge, Color, Icon, LoadingGroup, LoadingSkeleton } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold, Subtitle } from "capitalrx-components-2.0/lib/typography";
import { MultiSelectDropdown } from "capitalrx-components-2.0/lib/forms";

import { formatDollars, getClaimProviderName } from "@/App/utils/utils";
import { StackOrShelf } from "@/App/components/StackOrShelf";
import { useIsDentalUser, useIsPharmacyUser, useIsUnifiedUser, useIsVisionUser, useNavigateClient } from "@/App/hooks";
import { ActivityTypeMap, useGetMemberRecentActivityDataQuery } from "@/App/api/recentActivity";

import { ActivityCard } from "./ActivityCard";
import { MemberEligibilityContext } from "@/App/context/MemberEligibilityContext";
import { CLAIM, DENTAL, IN_PROGRESS_OUTCOME, MEDICAL, MEDICAL_CLAIM, PENDED_STATUS, PHARMACY, PRIOR_AUTHORIZATION, RecentActivityFilters, VISION } from "@/App/constants/constants";
import { NoResults } from "@/App/components";

const TextMultiSelectDropdown = styled(MultiSelectDropdown)`
  &:focus-visible {
    outline: 0!important;
  }
  div[role="combobox"] {
    @media screen and (min-width: 768px) {
      min-width: 175px;
    }
    
    & > .control {
      border: none;

      & > span {
        display: none;
      }

      &.focused {
        box-shadow: initial;
      }

      & .value-container > div {
        font-size: 15px;
        line-height: 20px;
        color: ${Color.toString(Color.Turqoise["500"])}};
        font-weight: 600;
      }
    }
  }
  & .badge-container, & .clear-container {
    display: none!important;
  }
`;

const CLAIMS_PAGE_TRANSLATION_PATH = "DASHBOARD.recentActivityDashboard";

const ActivityBadge = ({ activity, index, handleBadgeClose }: {activity: RecentActivityFilters, index: number, handleBadgeClose: (index: number) => void}) => {
  const { t: translate } = useTranslation();

  if (activity === PRIOR_AUTHORIZATION) {
    return <Badge type="system-alternative" onClick={() => handleBadgeClose(index)}>{translate(`${CLAIMS_PAGE_TRANSLATION_PATH}.priorAuthorization`)}</Badge>;
  } else if (activity === MEDICAL) {
    return <Badge type="success" onClick={() => handleBadgeClose(index)}>{translate(`${CLAIMS_PAGE_TRANSLATION_PATH}.medicalClaim`)}</Badge>;
  } else if (activity === PHARMACY) {
    return <Badge type="system" onClick={() => handleBadgeClose(index)}>{translate(`${CLAIMS_PAGE_TRANSLATION_PATH}.pharmacyClaim`)}</Badge>;
  } else if (activity === DENTAL) {
    return <Badge type="info" onClick={() => handleBadgeClose(index)}>{translate(`${CLAIMS_PAGE_TRANSLATION_PATH}.dentalClaim`)}</Badge>;
  } else {
    return <Badge type="info" onClick={() => handleBadgeClose(index)}>{translate(`${CLAIMS_PAGE_TRANSLATION_PATH}.visionClaim`)}</Badge>;
  }
};

const TRANSLATION_PATH = "DASHBOARD.recentActivityDashboard";

export const RecentActivity = () => {
  const { t: translate } = useTranslation();
  const [claimsType, setClaimsType] = useState<RecentActivityFilters[] | null>();
  const { memberEligibility } = useContext(MemberEligibilityContext);
  const navigate = useNavigateClient();
  const { isUnifiedUser } = useIsUnifiedUser();
  const { isDentalUser } = useIsDentalUser();
  const { isVisionUser } = useIsVisionUser();
  const { isPharmacyUser } = useIsPharmacyUser();
  const hasMedicalBenefits = isUnifiedUser;

  const { data: recentActivityData, isLoading } = useGetMemberRecentActivityDataQuery({ recent_activity_filters: claimsType || undefined });

  const claimFilterTypes = [
    { type: MEDICAL, value: translate(`${TRANSLATION_PATH}.medicalClaim`) },
    { type: PHARMACY, value: translate(`${TRANSLATION_PATH}.pharmacyClaim`) },
    { type: VISION, value: translate(`${TRANSLATION_PATH}.visionClaim`) },
    { type: DENTAL, value: translate(`${TRANSLATION_PATH}.dentalClaim`) },
    { type: PRIOR_AUTHORIZATION, value: translate(`${TRANSLATION_PATH}.priorAuthorization`) },
  ];

  const availableFilters = claimFilterTypes.filter(({ type }) => {
    if (type === PHARMACY) {
      return isPharmacyUser;
    }
    if (type === MEDICAL) {
      return hasMedicalBenefits;
    }
    if (type === DENTAL) {
      return isDentalUser;
    }
    if (type === VISION) {
      return isVisionUser;
    }
    return true;
  });

  let allActivity: ActivityTypeMap[] = [] as ActivityTypeMap[];
  if (recentActivityData && recentActivityData?.results.length > 0) {
    allActivity = recentActivityData.results.map((activity) => {
      if (activity.type === CLAIM) {
        return ({
            id: activity.id,
            type: activity.type,
            sortable_date: activity.sortable_date,
            title: activity.data.drug_name || "",
            description:
              <Body>{translate(`${TRANSLATION_PATH}.yourPharmacyClaim`)}
                <Semibold>{ formatDollars(activity.data.patient_pay_amount || "--") }</Semibold>.
              </Body>,
            data: activity.data,
            link: () => navigate(`/claims-history/pharmacy/${activity.id}/${memberEligibility.member.person_code}`, { state: { previousPage: "home" } }),
          });
        } else if (activity.type === PRIOR_AUTHORIZATION) {
          return ({
            id: activity.id,
            type: activity.type,
            sortable_date: activity.sortable_date,
            title: activity.data.drug_name || "",
            description:
              <Body>{translate(`${TRANSLATION_PATH}.${activity.data?.outcome ? "yourPriorAuthorization" : "yourInProgressPA"}`)}
                <Semibold>{translate(`${TRANSLATION_PATH}.${activity.data?.outcome || IN_PROGRESS_OUTCOME}`)} </Semibold>
              </Body>,
            data: activity.data,
            link: () => navigate(`/prior-authorization/${activity.id}`, { state: { previousPage: "home" } }),
          });
        } else {
          return ({
            id: activity.id,
            type: activity.type,
            sortable_date: activity.sortable_date,
            title: getClaimProviderName(
              activity.data?.billing_provider_first_name,
              activity.data?.billing_provider_last_name_or_organizational_name,
            ),
            description:
              <Body>{activity.data.status === PENDED_STATUS ? translate(`${TRANSLATION_PATH}.yourClaimInProgress`) : translate(`${TRANSLATION_PATH}.yourClaim`)}
                <Semibold>{translate(`${TRANSLATION_PATH}.${activity.data.status}`)} </Semibold>
                {activity.data.status === "denied" && translate(`${TRANSLATION_PATH}.viewDeniedClaim`)}
              </Body>,
            data: activity.data,
            link: () => navigate(`/claims-history/${activity.type === MEDICAL_CLAIM ? "medical" : activity.type }/${activity.id}/${memberEligibility.member.person_code}`, { state: { previousPage: "home" } }),
          });
        }
      });
    }

  const handleFilterSelection = (newValue: RecentActivityFilters[] | null | undefined) => {
    setClaimsType(newValue);
  };

  const handleBadgeClose = (index: number) => {
    if (claimsType && claimsType.length > 0) {
      setClaimsType(claimsType.toSpliced(index, 1));
    }
  };

  return (
    <Stack spacing="large">
      <Stack spacing="medium">
        <Stack spacing="small">
          <Subtitle testId="recent-activity-title">
            {translate(`${TRANSLATION_PATH}.recentActivityTitle`)}
          </Subtitle>
          <Body>
            {translate(`${TRANSLATION_PATH}.recentActivitySubtitle`)}
          </Body>
        </Stack>
          <Stack spacing="small">
            <StackOrShelf>
              <Shelf spacing="none">
                <Icon name="filter" color={Color.Turqoise["500"]} />
                {/* @ts-ignore */}
                <TextMultiSelectDropdown placeholder={translate(`${TRANSLATION_PATH}.filter`)} hideLabel onChange={handleFilterSelection} value={claimsType}>
                  {
                    availableFilters.map(({ type, value }) => <TextMultiSelectDropdown.Option key={type} value={type}>{value}</TextMultiSelectDropdown.Option>)
                  }
                </TextMultiSelectDropdown>
              </Shelf>
              <Shelf.Spacer />
            </StackOrShelf>
            <Shelf spacing="small">
              {!!claimsType && claimsType.map((activity, index) => <ActivityBadge key={activity} activity={activity} index={index} handleBadgeClose={handleBadgeClose} />)}
              <Shelf.Spacer />
            </Shelf>
          </Stack>
      </Stack>
      <LoadingGroup loading={isLoading}>
        <LoadingGroup.Placeholder
          fallback={
            <Stack>
              <LoadingSkeleton style={{ height: "170px", width: "100%" }} />
              <LoadingSkeleton style={{ height: "170px", width: "100%" }} />
              <LoadingSkeleton style={{ height: "170px", width: "100%" }} />
            </Stack>
          }
        >
          <Stack>
            {
              allActivity && allActivity?.length > 0 && allActivity.map((activity) => <ActivityCard key={activity.id} id={activity.id} activity={activity} />)
            }
            {
              !allActivity || allActivity?.length === 0 && <NoResults message={translate(`${TRANSLATION_PATH}.noRecentActivity`)} />
            }
          </Stack>
        </LoadingGroup.Placeholder>
      </LoadingGroup>
    </Stack>
  );
};
